import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { Avatar } from 'components/Avatar';
import { Card } from 'components/Card';
import { CardRow } from 'components/CardRow';
import { OrganisationLogoButtons } from 'screens/OrganisationScreen/OrganisationLogoButtons';
import { ICONS } from 'components/Icon';
import {
  ModalScreens,
  NavigationStacks,
  Screens,
  Stacks,
} from 'components/Navigation/types';
import { NavigationHeader } from 'components/NavigationHeader';
import { PageScrollView } from 'components/Page';
import { useScreenSize } from 'hooks/useScreenSize';
import { useSelector } from 'hooks/useSelector';
import { filterArrayEmpty } from 'lib/riskbase';
import { smartText } from 'lib/str';
import { theme } from 'lib/theme';
import React from 'react';
import { View } from 'react-native';
import { useDispatch } from 'react-redux';
import {
  NAVIGATE_MODAL,
  doSetupOrganisationClose,
  doSetupOrganisationOnBehalfOfOpen,
} from 'store/actions';
import { selectUserSelectedOrganisation } from 'store/auth/selectors';
import { selectSetupOrganisation } from 'store/remote/selectors';
import { SUPPORT_EMAIL } from 'types/types';

type Props = NativeStackScreenProps<
  NavigationStacks[Stacks.SETTINGS],
  Screens.SETTINGS_ORGANISATION
>;

const TxtBillingContactExplanation = smartText(
  `Invoices will be sent to the emails above. Please contact ${SUPPORT_EMAIL} to change your billing info`,
);

export const OrganisationScreen: React.FC<Props> = ({ navigation }) => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();

  const organisation = useSelector(selectUserSelectedOrganisation);
  const setupOrganisation = useSelector(selectSetupOrganisation);

  React.useEffect(() => {
    return () => {
      dispatch(doSetupOrganisationClose());
    };
  }, [dispatch]);

  if (!organisation || !setupOrganisation) {
    return null;
  }

  const {
    editName,
    editAddress,
    editPhone,
    editWebsite,
    editBilling,
    editOnBehalfOf,
    editLogo,
  } = setupOrganisation.userFeatures;
  const editDetails = editName || editAddress || editPhone || editWebsite;

  return (
    <PageScrollView
      isInModal={false}
      contentPaddingTop={0}
      header={
        <NavigationHeader
          heading={organisation.address.name}
          onPressBackButton={
            screenSize === 'large' ? undefined : navigation.goBack
          }
        />
      }
    >
      <View
        style={{
          alignItems: 'center',
          flex: 1,
          flexDirection: 'column',
          paddingHorizontal: theme.paddingHorizontal,
          paddingVertical: theme.paddingVertical,
        }}
      >
        <Avatar
          size={200}
          data={
            organisation.svgLogo
              ? { svgData: organisation.svgLogo }
              : organisation.pngLogo
                ? { localUrl: organisation.pngLogo }
                : null
          }
          imageBackgroundColor={
            organisation.pngLogo || organisation.svgLogo ? 'white' : undefined
          }
          icon={ICONS.HouseBuildingLight}
          imagePadding={20}
          svgPadding={20}
          iconPadding={40}
          resizeMode="contain"
        />
        {editLogo && (
          <View
            style={{
              alignItems: 'center',
              flexDirection: 'row',
              marginTop: -theme.grid * 2,
            }}
          >
            <OrganisationLogoButtons />
          </View>
        )}
      </View>

      <Card
        label="Details"
        labelLinkText="Change"
        onLabelLinkPress={
          editDetails
            ? () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.SettingsOrganisationDetailsModal,
                    params: {
                      organisationId: organisation.id,
                      name: organisation.address.name,
                      address1: organisation.address.address1,
                      address2: organisation.address.address2,
                      city: organisation.address.city,
                      county: organisation.address.county,
                      postcode: organisation.address.postcode,
                      phone: setupOrganisation.phone,
                      website: setupOrganisation.website,
                      approveRequireAddress:
                        setupOrganisation.approveRequireAddress,
                      approveRequireLogo: setupOrganisation.approveRequireLogo,
                      approveRequirePhone:
                        setupOrganisation.approveRequirePhone,
                      approveRequireWebsite:
                        setupOrganisation.approveRequireWebsite,
                      userFeatures: {
                        editName: setupOrganisation.userFeatures.editName,
                        editAddress: setupOrganisation.userFeatures.editAddress,
                        editPhone: setupOrganisation.userFeatures.editPhone,
                        editWebsite: setupOrganisation.userFeatures.editWebsite,
                        editApproveRequires:
                          setupOrganisation.userFeatures.editApproveRequires,
                      },
                    },
                  }),
                )
            : undefined
        }
      >
        <CardRow
          label={organisation.address.name}
          label2={[
            organisation.address.address1,
            organisation.address.address2,
            organisation.address.city,
            organisation.address.county,
            organisation.address.postcode,
          ]
            .filter(filterArrayEmpty)
            .join(', ')}
        />
        <CardRow value={setupOrganisation.phone} placeholder="No Phone" />
        <CardRow value={setupOrganisation.website} placeholder="No Website" />
      </Card>

      <Card
        label="Billing Details"
        labelLinkText="Change"
        onLabelLinkPress={
          editBilling
            ? () =>
                dispatch(
                  NAVIGATE_MODAL({
                    screen: ModalScreens.SettingsOrganisationBillingModal,
                    params: {
                      organisationId: organisation.id,
                      name: setupOrganisation.billingAddress.name,
                      address1: setupOrganisation.billingAddress.address1,
                      address2: setupOrganisation.billingAddress.address2,
                      city: setupOrganisation.billingAddress.city,
                      county: setupOrganisation.billingAddress.county,
                      postcode: setupOrganisation.billingAddress.postcode,
                      emails: setupOrganisation.billingEmails,
                      emailsPo: setupOrganisation.billingEmailsPo,
                    },
                  }),
                )
            : undefined
        }
        explanation={!editBilling ? TxtBillingContactExplanation : undefined}
      >
        <CardRow
          label={setupOrganisation.billingAddress.name}
          label2={[
            setupOrganisation.billingAddress.address1,
            setupOrganisation.billingAddress.address2,
            setupOrganisation.billingAddress.city,
            setupOrganisation.billingAddress.county,
            setupOrganisation.billingAddress.postcode,
          ]
            .filter(filterArrayEmpty)
            .join(', ')}
        />
        <CardRow
          label="Emails"
          value={setupOrganisation.billingEmails.split(',').join(', ')}
          placeholder="No Emails"
          labelAlignTop
        />
        <CardRow
          label="PO Emails"
          value={setupOrganisation.billingEmailsPo.split(',').join(', ')}
          placeholder="No PO Emails"
          labelAlignTop
        />
      </Card>

      {(editOnBehalfOf || !!setupOrganisation.onBehalfOf.length) && (
        <Card label="Perform Assessments On Behalf Of">
          <CardRow
            value={setupOrganisation.onBehalfOf.map((c) => c.name).join(', ')}
            placeholder="No Organisations"
            onPress={
              editOnBehalfOf
                ? () => dispatch(doSetupOrganisationOnBehalfOfOpen())
                : undefined
            }
          />
        </Card>
      )}
    </PageScrollView>
  );
};
